import { Link } from "react-router-dom";
import HighlightBox from "../../components/HighlightBox";

import demo from "./gif/demo.gif";
import wallUse from "./gif/wall-use.gif";
import benchmarkResult from "./image/modify_with_box.png";
import aiFuture from "./image/modify2_with_box.png";

export default function View() {
  return (
    <main style={pageStyle}>
      <Link to="/">← Back</Link>

      <section style={heroStyle}>
        <p style={eyebrowStyle}>Personal Project · Game AI Agent</p>

        <h1 style={titleStyle}>Tactics Game Agent MVP</h1>

        <p style={subtitleStyle}>
          Fixed tactics fail differently. Wall-aware works well on simple maps,
          composite handles harder maps better, and adaptive selection reduces
          the downside by switching between them.
        </p>

        {/* Demo Website Link */}
        <div style={linkBoxStyle}>
          <a
            href="https://junseongahn.github.io/tactical-game-agent/"
            target="_blank"
            rel="noreferrer"
            style={primaryLinkStyle}
          >
            Open Demo Website →
          </a>
        </div>

        <img src={demo} alt="Tactics game agent demo" style={heroImageStyle} />

        <p style={captionStyle}>
          The agent chooses actions from goal position, enemy pressure, wall
          structure, and threat zones.
        </p>
      </section>

      <HighlightBox>
        <h2>TL;DR</h2>
        <p style={bodyStyle}>
          I built a small inspectable game-agent system, compared fixed tactics,
          found their failure trade-off, and used AI-assisted adaptive logic to
          improve tactic selection.
        </p>
      </HighlightBox>

      <section style={sectionStyle}>
        <h2>Agent Loop</h2>
        <div style={loopBoxStyle}>
          observation → state understanding → tactic selection → action →
          evaluation
        </div>
      </section>

      <section style={sectionStyle}>
        <h2>Wall-Use Example</h2>

        <img src={wallUse} alt="Wall-use tactic replay" style={imageStyle} />

        <p style={bodyStyle}>
          The wall-aware tactic does not simply move straight to the goal. It
          uses nearby obstacles and safer inner routes to reduce enemy exposure
          while still progressing toward the goal.
        </p>

        <div style={gridStyle}>
          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>Turn 3</h3>
            <p style={cardBodyStyle}>
              Uses a nearby obstacle as cover instead of taking the shortest
              open path.
            </p>
          </article>

          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>Turn 6</h3>
            <p style={cardBodyStyle}>
              Moves toward the goal through a safer inner route.
            </p>
          </article>

          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>Turn 7</h3>
            <p style={cardBodyStyle}>
              Escapes immediate enemy pressure while staying goal-directed.
            </p>
          </article>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2>Compared Tactics</h2>

        <div style={gridStyle}>
          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>Wall-Aware</h3>
            <p style={cardBodyStyle}>
              Simple wall-use logic. Strong when the safe route is clear, but
              weaker on harder maps.
            </p>
          </article>

          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>Composite</h3>
            <p style={cardBodyStyle}>
              Combines goal, enemy, threat, health, and obstacle signals. Better
              on harder maps, but sometimes too conservative on easy maps.
            </p>
          </article>

          <article style={cardStyle}>
            <h3 style={cardTitleStyle}>AI Adaptive</h3>
            <p style={cardBodyStyle}>
              Uses an AI-assisted difficulty estimate to choose between
              wall-aware and composite behavior.
            </p>
          </article>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2>Adaptive Difficulty Logic</h2>

        <p style={bodyStyle}>
          After seeing the trade-off, I gave the failure pattern to AI and used
          it to design a simple difficulty function.
        </p>

        <pre style={codeStyle}>
          {`difficulty =
  unsafe path
+ enemy-controlled direct route
+ close enemy pressure
+ multiple enemies
+ weak wall / obstacle cover`}
        </pre>

        <p style={bodyStyle}>
          Easy state → use wall-aware. Hard state → use composite. This creates
          a simple adaptive selector instead of relying on one fixed tactic.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2>Benchmark Result</h2>

        <p style={bodyStyle}>
          Adaptive is not always the best tactic. The key result is that it
          stays above the weaker baseline between wall-aware and composite. In
          some cases, it also performs better than both.
        </p>

        <img
          src={benchmarkResult}
          alt="Benchmark result showing adaptive compared with wall-aware and composite"
          style={imageStyle}
        />

        <p style={captionStyle}>
          The highlighted boxes show the main comparison: adaptive avoids the
          weaker side of the wall-aware / composite trade-off.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2>Future Work</h2>

        <p style={bodyStyle}>
          In some runs, AI-assisted tactics perform better than both fixed
          baselines. I do not fully know why yet. The next step is to inspect
          replay traces and compare which actions were selected differently.
        </p>

        <img
          src={aiFuture}
          alt="AI-assisted tactic outperforming fixed baselines"
          style={imageStyle}
        />

        <p style={bodyStyle}>
          A natural extension is to replace the hand-written difficulty
          estimator with a small neural network that learns from replay outcomes
          and predicts which tactic is most likely to survive or reach the goal.
        </p>
      </section>

      <section style={{ ...sectionStyle, marginBottom: 80 }}>
        <h2>Why It Matters</h2>

        <p style={bodyStyle}>
          This project is intentionally small, but it shows the core system
          around game agents: state representation, interpretable tactic
          selection, replay inspection, and benchmark-based iteration.
        </p>
      </section>

      {/* Links Section */}
      <section style={{ ...sectionStyle, marginBottom: 80 }}>
        <h2>Links</h2>

        <p style={bodyStyle}>
          <a
            href="https://junseongahn.github.io/tactical-game-agent/"
            target="_blank"
            rel="noreferrer"
          >
            Demo Website
          </a>
          {" · "}
          <a
            href="https://github.com/JunseongAHN/tactical-game-agent"
            target="_blank"
            rel="noreferrer"
          >
            GitHub Repository
          </a>
        </p>
      </section>
    </main>
  );
}

const pageStyle: React.CSSProperties = {
  maxWidth: 1180,
  margin: "0 auto",
  padding: "32px 24px",
};

const heroStyle: React.CSSProperties = {
  marginTop: 36,
};

const eyebrowStyle: React.CSSProperties = {
  color: "#666",
  marginBottom: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 44,
  lineHeight: 1.05,
  margin: "0 0 14px",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 19,
  lineHeight: 1.6,
  color: "#333",
  maxWidth: 920,
  margin: 0,
};

const linkBoxStyle: React.CSSProperties = {
  marginTop: 20,
};

const primaryLinkStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "11px 16px",
  borderRadius: 12,
  border: "1px solid #222",
  color: "#111",
  textDecoration: "none",
  fontWeight: 700,
  fontSize: 15,
};

const secondaryLinkStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "11px 16px",
  borderRadius: 12,
  border: "1px solid #222",
  color: "#111",
  textDecoration: "none",
  fontWeight: 700,
  fontSize: 15,
  marginLeft: 20,
};

const heroImageStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 28,
  borderRadius: 16,
  border: "1px solid #ddd",
  background: "#f7f7f7",
};

const captionStyle: React.CSSProperties = {
  color: "#666",
  fontSize: 14,
  lineHeight: 1.5,
  marginTop: 10,
};

const sectionStyle: React.CSSProperties = {
  marginTop: 52,
};

const bodyStyle: React.CSSProperties = {
  fontSize: 17,
  lineHeight: 1.7,
  color: "#444",
  maxWidth: 900,
};

const loopBoxStyle: React.CSSProperties = {
  marginTop: 16,
  padding: "18px 20px",
  border: "1px solid #ddd",
  borderRadius: 14,
  background: "#fafafa",
  fontSize: 18,
  fontWeight: 600,
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
  marginTop: 18,
};

const cardStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  borderRadius: 14,
  padding: 18,
  background: "#fff",
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: 18,
  margin: "0 0 8px",
};

const cardBodyStyle: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.6,
  color: "#555",
  margin: 0,
};

const codeStyle: React.CSSProperties = {
  marginTop: 18,
  padding: 20,
  borderRadius: 14,
  border: "1px solid #ddd",
  background: "#111827",
  color: "#f9fafb",
  fontSize: 15,
  lineHeight: 1.7,
  overflowX: "auto",
};

const imageStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 20,
  borderRadius: 16,
  border: "1px solid #ddd",
  background: "#f7f7f7",
};
